import React from 'react';
import { useQuote } from '../../contexts/QuoteContext';
import type { Upgrade, ProductConfig, SelectedUpgrade } from '../../types/quote';
import productConfigData from '../../data/productConfig.json';
import QuantitySelector from '../QuantitySelector';

// Type assertion for the imported JSON data
const productConfig = productConfigData as ProductConfig;

interface Step4UpgradesProps {
  isActive: boolean;
  isCompleted: boolean;
  isDisabled: boolean;
}

// Enhanced upgrade interface to include availability and configuration data
interface UpgradeOption extends Upgrade {
  included: boolean;
  variantRestriction?: string;
}

const Step4Upgrades: React.FC<Step4UpgradesProps> = ({ isActive, isCompleted, isDisabled }) => {
  const { quoteState, updateQuoteState } = useQuote();

  // Get available upgrades based on selected model and variant
  const getAvailableUpgrades = (): UpgradeOption[] => {
    if (!quoteState.selectedProduct || !quoteState.selectedModel || !quoteState.selectedVariant) {
      return [];
    }

    const productType = quoteState.selectedProduct;
    const modelId = quoteState.selectedModel.id;
    const variantId = quoteState.selectedVariant.id;

    // Get upgrades from config
    const modelData = productConfig.productTypes[productType].models[modelId];
    if (!modelData || !modelData.upgrades) {
      return [];
    }

    return Object.values(modelData.upgrades)
      .filter((upgrade: Upgrade) => {
        // If upgrade has variant restriction, only include it if it matches the selected variant
        if (upgrade.variantRestriction) {
          return upgrade.variantRestriction === variantId;
        }
        // If no variant restriction, include the upgrade
        return true;
      })
      .map((upgrade: Upgrade) => {
        // Check if upgrade is available for this variant
        let included = upgrade.included || false;

        return {
          id: upgrade.id,
          name: upgrade.name,
          description: upgrade.description,
          price: upgrade.price,
          category: upgrade.category,
          included,
          variantRestriction: upgrade.variantRestriction,
          maxQuantity: upgrade.maxQuantity
        };
      });
  };

  const availableUpgrades = getAvailableUpgrades();

  const handleUpgradeQuantityChange = (upgrade: UpgradeOption, quantity: number) => {
    // Don't allow changes to included upgrades
    if (upgrade.included) {
      return;
    }

    const existingUpgrade = quoteState.selectedUpgrades.find(u => u.id === upgrade.id);

    if (quantity === 0) {
      // Remove upgrade if quantity is 0
      const newUpgrades = quoteState.selectedUpgrades.filter(u => u.id !== upgrade.id);
      updateQuoteState({ selectedUpgrades: newUpgrades });
    } else {
      // Add or update upgrade with new quantity
      const upgradeData: SelectedUpgrade = {
        id: upgrade.id,
        name: upgrade.name,
        description: upgrade.description,
        price: upgrade.price,
        category: upgrade.category,
        quantity: quantity
      };

      if (existingUpgrade) {
        // Update existing upgrade
        const newUpgrades = quoteState.selectedUpgrades.map(u =>
          u.id === upgrade.id ? upgradeData : u
        );
        updateQuoteState({ selectedUpgrades: newUpgrades });
      } else {
        // Add new upgrade
        const newUpgrades = [...quoteState.selectedUpgrades, upgradeData];
        updateQuoteState({ selectedUpgrades: newUpgrades });
      }
    }
  };

  const getUpgradeQuantity = (upgradeId: string): number => {
    const selectedUpgrade = quoteState.selectedUpgrades.find(u => u.id === upgradeId);
    return selectedUpgrade ? selectedUpgrade.quantity : 0;
  };

  const isUpgradeSelected = (upgradeId: string): boolean => {
    return getUpgradeQuantity(upgradeId) > 0;
  };

  // Check if there are no upgrades available
  const hasNoUpgrades = quoteState.selectedProduct && quoteState.selectedModel && quoteState.selectedVariant && availableUpgrades.length === 0;

  return (
    <div className={`step-section ${isActive ? 'active' : isDisabled ? 'disabled' : 'inactive'} ${!isActive && !isCompleted ? 'collapsed' : ''}`}>
      <div className="step-header">
        <div className={`step-number ${isCompleted ? 'completed' : isActive ? 'active' : 'disabled'}`}>
          <span>4</span>
        </div>
        <h2 className="step-title">Select Optional Upgrades</h2>
      </div>

      {(isActive || isCompleted) && (
        <>
          {hasNoUpgrades ? (
            <div className="no-upgrades-message">
              <p>There are no upgrade options for this model</p>
            </div>
          ) : (
            <div className="upgrades-list">
              {availableUpgrades.map((upgrade) => {
                const isSelected = isUpgradeSelected(upgrade.id);
                const isIncluded = upgrade.included;
                const quantity = getUpgradeQuantity(upgrade.id);
                const canSelect = !isIncluded;
                const hasQuantitySelector = upgrade.maxQuantity && upgrade.maxQuantity > 1;

                return (
                  <div
                    key={upgrade.id}
                    className={`upgrade-option ${isSelected || isIncluded ? 'selected' : ''} ${isIncluded ? 'included' : ''}`}
                    style={{
                      cursor: canSelect ? 'pointer' : 'not-allowed',
                      opacity: isIncluded ? 0.7 : 1
                    }}
                    onClick={() => {
                      // Only make the entire row clickable for single checkbox upgrades
                      if (!hasQuantitySelector && canSelect) {
                        handleUpgradeQuantityChange(upgrade, isSelected ? 0 : 1);
                      }
                    }}
                  >
                    <div className="upgrade-checkbox-container">
                      {hasQuantitySelector ? (
                        <QuantitySelector
                          value={isIncluded ? 1 : quantity}
                          min={0}
                          max={upgrade.maxQuantity || 10}
                          onChange={(newQuantity) => handleUpgradeQuantityChange(upgrade, newQuantity)}
                          disabled={!canSelect}
                        />
                      ) : (
                        <div>
                          {(isSelected || isIncluded) ? (
                            <div className="upgrade-checkbox checked">
                              <svg width="10" height="11" viewBox="0 0 11 11" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <g clipPath="url(#clip0_34_2785)">
                                  <path d="M10.7045 2.2644C10.9835 2.53296 10.9835 2.96909 10.7045 3.23765L4.99016 8.73765C4.71115 9.0062 4.25802 9.0062 3.979 8.73765L1.12186 5.98765C0.842843 5.71909 0.842843 5.28296 1.12186 5.0144C1.40088 4.74585 1.854 4.74585 2.13302 5.0144L4.4857 7.27671L9.69552 2.2644C9.97454 1.99585 10.4277 1.99585 10.7067 2.2644H10.7045Z" fill="white"/>
                                </g>
                                <defs>
                                  <clipPath id="clip0_34_2785">
                                    <path d="M0.914062 0H10.9141V11H0.914062V0Z" fill="white"/>
                                  </clipPath>
                                </defs>
                              </svg>
                            </div>
                          ) : (
                            <div className="upgrade-checkbox unchecked"></div>
                          )}
                        </div>
                      )}

                      <div className="upgrade-details">
                        <div className="upgrade-name">{upgrade.name}</div>
                        <div className="upgrade-description" style={{ color: '#666', fontSize: '0.9em', marginTop: '2px' }}>{upgrade.description}</div>
                      </div>
                    </div>

                    <div className="upgrade-price">
                      {isIncluded ? 'Included' : `$${upgrade.price.toLocaleString()}`}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Step4Upgrades;
